import { z } from "zod";

import { VibeAppStarterVersionSpecSchema } from "./app-starter.schemas";

/**
 * One private `@agent-nexus/*` package, served as its published `.tgz` so a
 * customer's app can vendor it.
 *
 * `GET /api/vibe/app-starter` covers the FIRST command of a build and nothing
 * after it: the bundle it serves is a new app. An app that already exists —
 * cloned from the org's git host, scaffolded before the bundle existed, or
 * simply built over weeks — resolves the library FROM THE REGISTRY in its own
 * `package.json`, so every later `npm install`, `npm ci` and library upgrade on
 * that machine 404s. This route is the answer to those moments: the backend
 * spends the platform's registry credential, and the caller receives bytes it
 * can commit.
 *
 * The credential never leaves the backend, and the package NAME is constrained
 * by the contract AND re-checked against the server's allowlist — the token
 * reads every private package in the scope, so a name that could arrive freely
 * from a caller would turn this into a general private-registry proxy.
 */

/**
 * The private packages a caller may ask for. Closed, and deliberately a
 * DIFFERENT list from the starter's: the starter is assembled server-side and
 * is not something a customer vendors into an app that already exists.
 */
export const VIBE_APP_PACKAGE_NAMES = ["@agent-nexus/apps-ui"] as const;

export const VibeAppPackageNameSchema = z.enum(VIBE_APP_PACKAGE_NAMES);

export type VibeAppPackageName = z.infer<typeof VibeAppPackageNameSchema>;

/**
 * What a caller that names no package gets. The component library is the one
 * every app depends on, so it is the answer to "vendor the private package
 * this app needs" in every case seen so far.
 */
export const VIBE_DEFAULT_APP_PACKAGE: VibeAppPackageName = "@agent-nexus/apps-ui";

export const DownloadVibeAppPackageQuerySchema = z.object({
  /** Which private package to serve. Absent means the component library. */
  package: VibeAppPackageNameSchema.optional(),
  /**
   * Exact version or `latest` — the same closed spelling the starter route
   * takes, and for the same reason: the value reaches an npm registry URL.
   */
  version: VibeAppStarterVersionSpecSchema.optional()
});

export type DownloadVibeAppPackageQuery = z.infer<typeof DownloadVibeAppPackageQuerySchema>;

/**
 * Response headers the tarball is served with. A client needs all three before
 * it can write a correct `vendor/` entry and a correct lockfile, and reading
 * them off the response is what keeps the two from being derived twice.
 *
 * `integrity` is the digest of the BYTES IN THIS RESPONSE, verified server-side
 * against the digest the registry published. A client that records it in a
 * lockfile is recording what it actually wrote to disk.
 */
export const VIBE_APP_PACKAGE_NAME_HEADER = "x-nexus-app-package-name";
export const VIBE_APP_PACKAGE_VERSION_HEADER = "x-nexus-app-package-version";
export const VIBE_APP_PACKAGE_INTEGRITY_HEADER = "x-nexus-app-package-integrity";
